import { ResourceItemType } from '@features/components/ResourceCards';
import { noop } from 'lodash-es';
import { toast } from 'react-toastify';
import { MdOutlineTripOrigin } from 'react-icons/md';
import { Case, CaseUserGroup, ClinicalItemType, EmployeeLabels, ResourceLogisticsStatus } from '@/api/apis/case';
import {
  csStatusName,
  resourceLogisticsStatusNames,
  RESOURCE_STATUS_TEXT_MAP,
  rsStatusName,
  employeeLabelName,
} from '@/constants/resources.constants';
import { getClinicalRecordByType } from '@/utils/helpers';

export const deviceDefaultDisabledKeys = [ResourceLogisticsStatus.Reserved, ResourceLogisticsStatus.Booked];

export const employeeDefaultDisabledKeys = [
  ResourceLogisticsStatus.Reserved,
  ResourceLogisticsStatus.BookingSent,
  ResourceLogisticsStatus.Booked,
  ResourceLogisticsStatus.DeployedToDonorHospital,
];

const blockingStatuses = [ResourceLogisticsStatus.Reserved, ResourceLogisticsStatus.Done];

export const getSurgeonDisabledKeys = (status: ResourceLogisticsStatus) => {
  if (blockingStatuses.includes(status)) {
    return [
      ...employeeDefaultDisabledKeys,
      ResourceLogisticsStatus.RecoveryIsCompleted,
      ResourceLogisticsStatus.AtDonorHospital,
      ResourceLogisticsStatus.DeployedFromDonorHospital,
    ];
  }
  return employeeDefaultDisabledKeys;
};

type GetCSDisabledKeysParams = {
  status: ResourceLogisticsStatus;
  isShadowAvailable: boolean;
  isProctorAvailable: boolean;
  currentEmployeeLabels: EmployeeLabels[];
  assignedLabelsSet: Set<EmployeeLabels>;
};

export const getCSDisabledKeys = ({
  status,
  isShadowAvailable,
  isProctorAvailable,
  currentEmployeeLabels,
  assignedLabelsSet,
}: GetCSDisabledKeysParams) => {
  const isPrimaryEmployee = currentEmployeeLabels.includes(EmployeeLabels.Primary);
  const isReliefEmployee = currentEmployeeLabels.includes(EmployeeLabels.Relief);
  const hasPrimaryOnCase = assignedLabelsSet.has(EmployeeLabels.Primary);
  const hasReliefOnCase = assignedLabelsSet.has(EmployeeLabels.Relief);

  const blockedLabels = [
    ...(hasPrimaryOnCase && !isPrimaryEmployee ? [EmployeeLabels.Primary] : []),
    ...(hasReliefOnCase && !isReliefEmployee ? [EmployeeLabels.Relief] : []),
    ...(isShadowAvailable ? [] : [EmployeeLabels.Shadow]),
    ...(isProctorAvailable ? [] : [EmployeeLabels.Proctor]),
  ];

  if (blockingStatuses.includes(status)) {
    return [
      ...blockedLabels,
      ...employeeDefaultDisabledKeys,
      ResourceLogisticsStatus.AtDonorHospital,
      ResourceLogisticsStatus.DeployedFromDonorHospital,
      ResourceLogisticsStatus.AtTXCenter,
      ResourceLogisticsStatus.DeployedFromTXCenter,
    ];
  }
  return [...blockedLabels, ...employeeDefaultDisabledKeys];
};

export const getDeviceDisabledKeys = (status: ResourceLogisticsStatus) => {
  if (status === ResourceLogisticsStatus.OCSDone) {
    return [...deviceDefaultDisabledKeys, ResourceLogisticsStatus.OCSDeployed];
  }
  return deviceDefaultDisabledKeys;
};

const planningStatusesItems: ResourceItemType[] = [
  {
    key: ResourceLogisticsStatus.Reserved,
    label: RESOURCE_STATUS_TEXT_MAP[ResourceLogisticsStatus.Reserved]!,
    color: 'primary',
    onUserMenuPress: noop,
  },
  {
    key: ResourceLogisticsStatus.BookingSent,
    label: RESOURCE_STATUS_TEXT_MAP[ResourceLogisticsStatus.BookingSent]!,
    color: 'primary',
    onUserMenuPress: noop,
  },
  {
    key: ResourceLogisticsStatus.Booked,
    label: RESOURCE_STATUS_TEXT_MAP[ResourceLogisticsStatus.Booked]!,
    color: 'primary',
    onUserMenuPress: noop,
  },
  {
    key: ResourceLogisticsStatus.DeployedToDonorHospital,
    label: RESOURCE_STATUS_TEXT_MAP[ResourceLogisticsStatus.DeployedToDonorHospital]!,
    color: 'primary',
    onUserMenuPress: noop,
  },
];

const changeCsStatusIfOnOcs = (caseData: Case, statusChangeCallback: () => void) => {
  const onOcs = getClinicalRecordByType(caseData.clinicalData, ClinicalItemType.OrganOnOCS);
  if (onOcs) statusChangeCallback();
  else toast.error('Perfusion start is not recorded, the Clinical Specialist’s status cannot be changed');
};

export const getCSMenuItems = (
  changeCsStatus: (status: ResourceLogisticsStatus) => void,
  setCsLabels: (newLabel: EmployeeLabels) => void,
  caseData: Case,
): ResourceItemType[] => {
  return [
    {
      key: 'markResourceAs',
      label: 'Mark Resource As',
      color: 'primary',
      children: [
        {
          key: EmployeeLabels.Primary,
          label: employeeLabelName[EmployeeLabels.Primary],
          startContent: (
            <div className="text-success-50">
              <MdOutlineTripOrigin size={16} />
            </div>
          ),
          color: 'primary',
          onUserMenuPress: () => setCsLabels(EmployeeLabels.Primary),
        },
        {
          key: EmployeeLabels.Relief,
          label: employeeLabelName[EmployeeLabels.Relief],
          startContent: (
            <div className="text-info-60">
              <MdOutlineTripOrigin size={16} />
            </div>
          ),
          color: 'primary',
          onUserMenuPress: () => setCsLabels(EmployeeLabels.Relief),
        },
        {
          key: EmployeeLabels.Proctor,
          label: employeeLabelName[EmployeeLabels.Proctor],
          startContent: (
            <div className="text-base-30">
              <MdOutlineTripOrigin size={16} />
            </div>
          ),
          color: 'primary',
          onUserMenuPress: () => setCsLabels(EmployeeLabels.Proctor),
        },
        {
          key: EmployeeLabels.Shadow,
          label: employeeLabelName[EmployeeLabels.Shadow],
          startContent: (
            <div className="text-base-40">
              <MdOutlineTripOrigin size={16} />
            </div>
          ),
          color: 'primary',
          onUserMenuPress: () => setCsLabels(EmployeeLabels.Shadow),
        },
      ],
    },
    {
      key: 'manageResourceStatus',
      label: 'Manage Resource Status',
      color: 'primary',
      children: [
        ...planningStatusesItems,
        {
          key: ResourceLogisticsStatus.AtDonorHospital,
          label: csStatusName[ResourceLogisticsStatus.AtDonorHospital]!,
          color: 'primary',
          onUserMenuPress: () => changeCsStatus(ResourceLogisticsStatus.AtDonorHospital),
        },
        {
          key: ResourceLogisticsStatus.DeployedFromDonorHospital,
          label: csStatusName[ResourceLogisticsStatus.DeployedFromDonorHospital]!,
          color: 'primary',
          onUserMenuPress: () => {
            const callback = () => changeCsStatus(ResourceLogisticsStatus.DeployedFromDonorHospital);
            changeCsStatusIfOnOcs(caseData, callback);
          },
        },
        {
          key: ResourceLogisticsStatus.AtTXCenter,
          label: csStatusName[ResourceLogisticsStatus.AtTXCenter]!,
          color: 'primary',
          onUserMenuPress: () => {
            const callback = () => changeCsStatus(ResourceLogisticsStatus.AtTXCenter);
            changeCsStatusIfOnOcs(caseData, callback);
          },
        },
        {
          key: ResourceLogisticsStatus.DeployedFromTXCenter,
          label: csStatusName[ResourceLogisticsStatus.DeployedFromTXCenter]!,
          color: 'primary',
          onUserMenuPress: () => {
            const callback = () => changeCsStatus(ResourceLogisticsStatus.DeployedFromTXCenter);
            changeCsStatusIfOnOcs(caseData, callback);
          },
        },
        {
          key: ResourceLogisticsStatus.Done,
          label: csStatusName[ResourceLogisticsStatus.Done]!,
          color: 'primary',
          onUserMenuPress: () => changeCsStatus(ResourceLogisticsStatus.Done),
        },
      ],
    },
  ];
};

export const getSurgeonMenuItems = (
  changeSurgeonStatus: (status: ResourceLogisticsStatus) => void,
): ResourceItemType[] => [
  ...planningStatusesItems,
  {
    key: ResourceLogisticsStatus.AtDonorHospital,
    label: rsStatusName[ResourceLogisticsStatus.AtDonorHospital]!,
    color: 'primary',
    onUserMenuPress: () => changeSurgeonStatus(ResourceLogisticsStatus.AtDonorHospital),
  },
  {
    key: ResourceLogisticsStatus.RecoveryIsCompleted,
    label: rsStatusName[ResourceLogisticsStatus.RecoveryIsCompleted]!,
    color: 'primary',
    onUserMenuPress: () => changeSurgeonStatus(ResourceLogisticsStatus.RecoveryIsCompleted),
  },
  {
    key: ResourceLogisticsStatus.DeployedFromDonorHospital,
    label: rsStatusName[ResourceLogisticsStatus.DeployedFromDonorHospital]!,
    color: 'primary',
    onUserMenuPress: () => changeSurgeonStatus(ResourceLogisticsStatus.DeployedFromDonorHospital),
  },
  {
    key: ResourceLogisticsStatus.Done,
    label: rsStatusName[ResourceLogisticsStatus.Done]!,
    color: 'primary',
    onUserMenuPress: () => changeSurgeonStatus(ResourceLogisticsStatus.Done),
  },
];

export const getDeviceMenuItems = (
  changeDeviceStatus: (status: ResourceLogisticsStatus) => void,
): ResourceItemType[] => [
  {
    key: ResourceLogisticsStatus.Reserved,
    label: resourceLogisticsStatusNames[ResourceLogisticsStatus.Reserved],
    color: 'primary',
    onUserMenuPress: noop,
  },
  {
    key: ResourceLogisticsStatus.Booked,
    label: resourceLogisticsStatusNames[ResourceLogisticsStatus.Booked],
    color: 'primary',
    onUserMenuPress: noop,
  },
  {
    key: ResourceLogisticsStatus.OCSDeployed,
    label: resourceLogisticsStatusNames[ResourceLogisticsStatus.OCSDeployed],
    color: 'primary',
    onUserMenuPress: () => changeDeviceStatus(ResourceLogisticsStatus.OCSDeployed),
  },
  {
    key: ResourceLogisticsStatus.OCSDone,
    label: resourceLogisticsStatusNames[ResourceLogisticsStatus.OCSDone],
    color: 'primary',
    onUserMenuPress: () => changeDeviceStatus(ResourceLogisticsStatus.OCSDone),
  },
];

export const externalIconGroups: CaseUserGroup[] = [
  CaseUserGroup.ClinicalCoordinators,
  CaseUserGroup.ClinicalLeads,
  CaseUserGroup.LogisticsManagers,
  CaseUserGroup.OrganManagementSpecialists,
];
